import { getConfig } from 'config/configuration';
import { AUDIT_TYPE } from 'interfaces/auditTypes';
import type { PolicyInstance } from 'interfaces/customerDataTypes';
import queryString from 'query-string';
import type { RootState } from 'store/configureStore';
import { sendAuditEvent } from 'utils';
import { HpDispatchFunction } from './actionTypes';
import { fetchResolver } from './fetchResolver';
import { OpenActionModalPayload, openFeatureNotAvailableModal } from './openModalAction';
import { redirectToPolicyPortalAction } from './redirectToPolicyPortalAction';

export type TransactionType = 'REN';

export const TRANSACTION_TYPE = { REN: 'REN' } as const;

export const QUESTION_SET = {
  RENEWAL_JOURNEY: 'renewal-journey',
  CHANGE_RENEWAL_JOURNEY: 'change-renewal-journey',
  AUDIT: 'policy-audit',
  NOTES: 'policy-notes'
} as const;

interface GetValidConvertibleInstancePathModel {
  username: string;
  transactionType: TransactionType;
  isAgent: boolean;
  questionSet: string;
  onError?: () => void;
}

interface ConvertibleInstance {
  historyId: number;
  policyDetailsId: string;
  modificationAllowed: boolean;
  modificationExcludedDescription: string;
  modifiedHistoryId: number;
  modifiedPolicyDetailsId: string;
}

const { websiteEndpoint } = getConfig();

export function isConversionAvailableForContext(
  instance: PolicyInstance,
  isAgent: boolean,
  questionSet: string
): boolean {
  if (questionSet === QUESTION_SET.RENEWAL_JOURNEY) {
    return isAgent ? instance.isAgentRenConversionAvailable : instance.isCustomerRenConversionAvailable;
  }

  if (questionSet === QUESTION_SET.CHANGE_RENEWAL_JOURNEY) {
    return isAgent ? instance.isEditableRenAgentConversionAvailable : instance.isEditableRenCustomerConversionAvailable;
  }

  return false;
}

export const getValidConvertibleInstanceAction = ({
  username,
  transactionType,
  isAgent,
  questionSet,
  onError
}: GetValidConvertibleInstancePathModel) => {
  return (
    dispatch: HpDispatchFunction<ConvertibleInstance[] | OpenActionModalPayload | string>,
    getState: () => RootState
  ): Promise<void> => {
    dispatch({ type: 'VALID_CONVERTIBLE_INSTANCE_LOADING' });

    const state = getState();
    const { policyInstances, policyNumber, policyDetailsId, historyId, email, correspondencePostcode } =
      state.customerData.selectedPolicy || {};
    const convertibleInstance =
      policyInstances &&
      policyInstances?.find((instance) => isConversionAvailableForContext(instance, isAgent, questionSet));

    if (!convertibleInstance) {
      dispatch(openFeatureNotAvailableModal(isAgent));
      sendAuditEvent({
        activityId: AUDIT_TYPE.RENEWAL_JOURNEY_ACCESS_VALIDATION_FAILURE,
        policyDetailsId,
        historyId,
        policyNumber,
        activityDescription: 'No convertible instance available'
      });
      return Promise.resolve();
    }

    const queryText = queryString.stringify({
      policyDetailsId: convertibleInstance.policyDetailsId,
      historyId: convertibleInstance.historyId,
      transactionType,
      username
    });
    const url = `${websiteEndpoint}/api/policy/valid-conversion-instance?${queryText}`;

    return fetchResolver<any>({
      options: { url, method: 'GET', description: `Get valid convertible instance` },
      dispatch,
      successAction: (response: ConvertibleInstance[]) => {
        const { modificationAllowed, modificationExcludedDescription, modifiedHistoryId, modifiedPolicyDetailsId } =
          response[0];
        if (modificationAllowed) {
          dispatch({ type: 'VALID_CONVERTIBLE_INSTANCE_SUCCESS', payload: response });
          redirectToPolicyPortalAction({
            policyDetailsId: modifiedPolicyDetailsId,
            historyId: modifiedHistoryId,
            policyNumber,
            questionSet,
            userName: username,
            isAgent,
            context: 'quote',
            emailAddress: email ?? '',
            postcode: correspondencePostcode ?? '',
            onError
          })(dispatch);
        } else {
          dispatch({ type: 'VALID_CONVERTIBLE_INSTANCE_ERROR', payload: modificationExcludedDescription });
          sendAuditEvent({
            activityId: AUDIT_TYPE.RENEWAL_JOURNEY_ACCESS_VALIDATION_FAILURE,
            policyDetailsId: convertibleInstance.policyDetailsId,
            historyId: convertibleInstance.historyId,
            policyNumber,
            activityDescription: modificationExcludedDescription
          });
          dispatch(openFeatureNotAvailableModal(isAgent, modificationExcludedDescription));
        }
      },
      failedAction: (err) => {
        dispatch({ type: 'VALID_CONVERTIBLE_INSTANCE_ERROR', payload: err.message });
        sendAuditEvent({
          activityId: AUDIT_TYPE.RENEWAL_JOURNEY_ACCESS_VALIDATION_FAILURE,
          policyDetailsId: convertibleInstance.policyDetailsId,
          historyId: convertibleInstance.historyId,
          policyNumber
        });
        dispatch(openFeatureNotAvailableModal(isAgent));
      }
    });
  };
};
